import React, { useEffect, useState } from 'react'
import styles from './p2pDownloadScheme.jcss'
import translate from '../../../core/translate'
import { colors } from '../../../theme'
import { cn } from 'ethcss'
import { Switch } from '../../../atoms/Switch'
import EditText from '../../../blocks.simple/editText/editText'
import fileSize from 'filesize'
import Icon from '../../../blocks.simple/icon/icon'
import { api } from '../../../core/api/ConnectionManager'

interface IDevice {
    id: number
    name: string
    status: 'online' | 'offline' | 'sleep' | 'error' | 'online_display_off' | 'updating'
    role: string
    filesInfo: any
}

type Master = {
    id: number
    enabled: boolean
    name: string
    storageUrl: string
}

export const P2PDownloadScheme = ({ p_ }: any) => {
    const [updateInterval, setUpdateInterval] = useState<string>('15')
    const [autoUpdate, setAutoUpdate] = useState<boolean>(true)
    const [devices, setDevices] = useState<IDevice[]>([])
    const [openedDevices, setOpenedDevices] = useState<number[]>([])
    const [masterInfo, setMasterInfo] = useState<Master | null>(null)
    const [statuses, setStatuses] = useState({
        online: 0,
        offline: 0,
        error: 0,
        sleep: 0,
    })

    const getDevicesList = () => {
        api.send('getP2PDownloads', { groupId: p_.id, openedDevicesId: openedDevices })
    }

    const getMasterInfo = () => {
        api.send<object, Master>('getP2PDownloadsMasterInfo', { groupId: p_.id }).then((res) => {
            if (res) {
                setMasterInfo(res)
            }
        })
    }

    const setDevicesStatuses = () => {
        setStatuses({
            online: p_.descendantStatusCount[0].count,
            offline: p_.descendantStatusCount[2].count,
            error: p_.descendantStatusCount[3].count,
            sleep: p_.descendantStatusCount[6].count,
        })
    }

    const p2pDownloadIsEnabled = () => {
        return masterInfo && masterInfo.enabled
    }

    const getMasterName = () => {
        return (masterInfo && masterInfo.name) || '—'
    }

    const getStorageUrl = () => {
        return (masterInfo && masterInfo.storageUrl) || '—'
    }

    const onOpenDevice = (id: number) => {
        if (openedDevices.some((deviceId) => deviceId === id)) {
            setOpenedDevices(openedDevices.filter((deviceId) => deviceId !== id))

            return
        }

        setOpenedDevices([...openedDevices, id])
    }

    const getRoleName = (role: string) => {
        switch (role) {
            case 'disabled':
                return <span style={{ color: colors.error }}>{translate('disabled')}</span>
            case 'unknown':
                return <span style={{ color: colors.error }}>{translate('notDetected')}</span>
            case 'master':
                return translate('p2p_master')
            case 'slave':
                return translate('p2p_client')
            default:
                return <span style={{ color: colors.error }}>{translate('notDetected')}</span>
        }
    }

    useEffect(() => {
        if (!autoUpdate) {
            return
        }

        const interval = setInterval(() => {
            getDevicesList()
        }, +updateInterval * 1000)

        return () => clearInterval(interval)
    }, [updateInterval, autoUpdate, openedDevices])

    useEffect(() => {
        if (openedDevices.length) {
            getDevicesList()
        }
    }, [openedDevices])

    useEffect(() => {
        let timeout: any = null

        if (+updateInterval < 15) {
            timeout = setTimeout(() => {
                setUpdateInterval('15')
            }, 500)
        }

        return () => clearTimeout(timeout)
    }, [updateInterval])

    useEffect(() => {
        getMasterInfo()
        getDevicesList()
    }, [])

    useEffect(() => {
        setDevicesStatuses()
    }, [p_.descendantStatusCount])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'p2pDownloadsReceived',
            (data: any) => {
                if (data) {
                    setDevices(data)
                }
            },
            listenersId
        )
        api.addObserver(
            'p2pDownloadsMasterInfoUpdated',
            (data: Master) => {
                if (data) {
                    setMasterInfo(data)
                }
            },
            listenersId
        )

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [])

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.title}>
                    {translate('p2pDownloadScheme')} {p_.name}
                </div>
                <div className={styles.statusList}>
                    <div className={styles.status} style={{ color: colors.online }}>
                        {translate('online')}: {statuses.online}
                    </div>
                    <div className={styles.status} style={{ color: colors.offline }}>
                        {translate('offline')}: {statuses.offline}
                    </div>
                    <div className={styles.status} style={{ color: colors.error }}>
                        {translate('error')}: {statuses.error}
                    </div>
                    <div className={styles.status} style={{ color: colors.sleep }}>
                        {translate('sleep')}: {statuses.sleep}
                    </div>
                </div>
                <div className={styles.headerSplit}>
                    <div className={styles.headerSplitLeft}>
                        <div className={styles.p2pStatusRow}>
                            {translate('p2pDownloadMode')}: {translate(p2pDownloadIsEnabled() ? 'enabled' : 'disabled')}
                        </div>
                        <div className={styles.p2pStatusRow}>
                            {translate('p2p_master')}: {getMasterName()}
                        </div>
                        <div className={styles.p2pStatusRow}>
                            {translate('storageUrl')}: {getStorageUrl()}
                        </div>
                    </div>
                    <div className={styles.headerSplitRight}>
                        <div className={styles.updateButton} onClick={getDevicesList}>
                            {translate('update')}
                        </div>
                        <div className={styles.switchWrapper}>
                            <div>{translate('autoUpdate')}</div>
                            <Switch
                                onChange={() => {
                                    setAutoUpdate(!autoUpdate)
                                }}
                                checked={autoUpdate}
                                className={styles.switch}
                            />
                        </div>
                        <div className={cn(styles.intervalWrapper, !autoUpdate ? styles.disabled : '')}>
                            <div>{translate('updateIntervalSec')}</div>
                            <EditText
                                type={'number'}
                                value={updateInterval}
                                onChange={(value: string) => {
                                    setUpdateInterval(value)
                                }}
                                containerClass={styles.intervalInput}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.displaysSection}>
                <div className={styles.displaysList}>
                    <div className={styles.displayHeader}>
                        <div className={cn(styles.displayCol, styles.col, styles.col_1)}>
                            {translate('displayName')}
                        </div>
                        <div className={cn(styles.displayCol, styles.col, styles.col_2)}>{translate('role')}</div>
                        <div className={cn(styles.displayCol, styles.col, styles.col_3)}>{translate('status')}</div>
                    </div>
                    {devices.map((display) => {
                        return (
                            <div className={styles.display}>
                                <div className={styles.displayInner}>
                                    <div className={cn(styles.displayCol, styles.col, styles.col_1)}>
                                        <div className={styles.deviceName}>
                                            <Icon
                                                type={openedDevices.includes(display.id) ? 'arrow_down' : 'arrow_right'}
                                                size={12}
                                                className={styles.deviceArrowIcon}
                                                onClick={() => onOpenDevice(display.id)}
                                            />
                                            <div>{display.name}</div>
                                        </div>
                                    </div>
                                    <div
                                        className={cn(styles.displayCol, styles.col, styles.col_2, styles.col_noBorder)}
                                    >
                                        {getRoleName(display.role)}
                                    </div>
                                    <div className={cn(styles.displayCol, styles.col, styles.col_3)}>
                                        <div style={{ color: colors[display.status] }}>{translate(display.status)}</div>
                                    </div>
                                </div>
                                {!!(display.filesInfo && display.filesInfo.length) && (
                                    <>
                                        <div className={styles.fileHeader}>
                                            <div
                                                className={cn(
                                                    styles.displayCol,
                                                    styles.col,
                                                    styles.col_1,
                                                    styles.col_inner
                                                )}
                                            >
                                                {translate('fileName')}
                                            </div>
                                            <div className={cn(styles.displayCol, styles.col, styles.col_2)}>
                                                {translate('downloadProgress')}
                                            </div>
                                            <div className={cn(styles.displayCol, styles.col, styles.col_3)}>
                                                {translate('fileAddress')}
                                            </div>
                                        </div>
                                        {display.filesInfo.map((file: any) => {
                                            return (
                                                <div className={styles.file} key={file.sourceId}>
                                                    <div
                                                        className={cn(
                                                            styles.displayCol,
                                                            styles.col,
                                                            styles.col_1,
                                                            styles.col_inner,
                                                            styles.col_borderBottom,
                                                            styles.col_borderLeft
                                                        )}
                                                    >
                                                        <div className={styles.fileName}>
                                                            <Icon type={file.type} className={styles.fileIcon} />
                                                            {file.fileName}
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={cn(
                                                            styles.displayCol,
                                                            styles.col,
                                                            styles.col_2,
                                                            styles.col_borderBottom
                                                        )}
                                                    >
                                                        {file.status === 'downloading' ? (
                                                            <>
                                                                {fileSize(file.downloadedBytes)} {translate('from')}{' '}
                                                                {fileSize(file.totalSizeBytes)} (
                                                                {file.downloadedPercent}%)
                                                            </>
                                                        ) : (
                                                            <div className={styles.downloadedWrapper}>
                                                                <div className={styles.downloadedLabel}>
                                                                    {translate('downloaded')}
                                                                </div>
                                                                <Icon
                                                                    type={'downloaded'}
                                                                    className={styles.fileIcon}
                                                                    size={16}
                                                                    color={'online'}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div
                                                        className={cn(
                                                            styles.displayCol,
                                                            styles.col,
                                                            styles.col_3,
                                                            styles.col_borderBottom,
                                                            styles.col_borderRight,
                                                            styles.wordBreak
                                                        )}
                                                    >
                                                        {file.fileAddress ? file.fileAddress : '—'}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
